import { useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import { useUpdateSocialAnamnesis } from '../../../../../api/generated/anamnesis-controller/anamnesis-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  AddressDtoAddressType,
  type GetAnamnesesByPatient200,
  PatientAddressDto,
  PatientAddressDtoReferenceType,
  SocialAnamnesisDto,
} from '../../../../../api/generated/model';
import { getGetAnamnesesByPatientQueryKey } from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { InstitutionContactFields } from '../../../../../components/form/InstitutionContactFields.tsx';
import { SingeLineFormGrid } from '../../../../../components/form/SingeLineFormComponents.tsx';
import { LeavePageWarning } from '../../../../../components/LeavePageWarning.tsx';
import { FieldMetaInfos, useAutoSaveForm } from '../../../../../hooks/useAutoSaveForm.ts';
import { getCurrentDate } from '../../../../../utils/date.ts';
import { getStringOrUndefined } from '../../../../../utils/string.ts';

export function EditAnamnesisInstitutionForm({
  socialAnamnesis,
  patientId,
  anamnesisId,
}: {
  socialAnamnesis: SocialAnamnesisDto;
  patientId: string;
  anamnesisId: string;
}) {
  const queryClient = useQueryClient();

  const updateSocialAnamnesisMutation = useUpdateSocialAnamnesis({
    mutation: {
      mutationKey: ['updateAnamnesisInstitution'],
      onSuccess: (data) =>
        queryClient.setQueryData<GetAnamnesesByPatient200>(
          getGetAnamnesesByPatientQueryKey(patientId),
          (oldAnamneses) =>
            oldAnamneses &&
            Object.fromEntries(
              Object.entries(oldAnamneses).map(([key, value]) => [
                key,
                value.id === anamnesisId ? { ...value, social: data } : value,
              ]),
            ),
        ),
      onError: () =>
        toast.error('Bei dem Speichern der Institution ist ein Fehler aufgetreten', {
          toastId: 'institutionSaveError',
        }),
    },
  });

  const {
    control,
    getFieldState,
    setValue,
    watch,
    formState: { isFormDirty },
    scheduleSave,
  } = useAutoSaveForm({
    sourceData: socialAnamnesis,
    getDefaultValues,
    save: (data) =>
      updateSocialAnamnesisMutation.mutateAsync({
        id: anamnesisId,
        data: {
          note: getStringOrUndefined(socialAnamnesis.note),
          housingStatus: socialAnamnesis.housingStatus,
          employmentStatus: socialAnamnesis.employmentStatus,
          maritalStatus: socialAnamnesis.maritalStatus,
          sportFrequency: socialAnamnesis.sportFrequency,
          dietType: socialAnamnesis.dietType,
          educationLevel: socialAnamnesis.educationLevel,
          immunosuppressionStatus: socialAnamnesis.immunosuppressionStatus,
          doctor: data.doctorAddress
            ? ({
                address: data.doctorAddress,
                contact: data.doctorContact ?? undefined,
                from: getCurrentDate(),
                patientId: patientId,
                referenceType: PatientAddressDtoReferenceType.ANAMNESIS_DOCTOR,
              } as PatientAddressDto)
            : undefined,
        },
      }),
    fieldMetaInfos,
    onValidationFailed: () =>
      toast.warn('Institution kann aufgrund eines Fehlers momentan nicht gespeichert werden', {
        toastId: 'institutionValidationError',
        autoClose: false,
      }),
    onValidationPassed: () => toast.dismiss('institutionValidationError'),
  });

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty && !updateSocialAnamnesisMutation.isPending,
    withResolver: true,
  });

  return (
    <>
      <SingeLineFormGrid>
        <InstitutionContactFields
          formMethods={{ control, watch, setValue, getFieldState }}
          layout="singleLine"
          addressField="doctorAddress"
          contactField="doctorContact"
          addressLabel="Institution"
          contactLabel="Ansprechperson"
          addressTypes={[AddressDtoAddressType.DOCTOR]}
          canChangeAddressType={false}
          variant="plain"
          clearContactOnAddressChange="always"
          contactClearOptions={false}
          useAllContactsList={true}
        />
      </SingeLineFormGrid>
      <LeavePageWarning
        open={blocker.status === 'blocked'}
        onDelete={() => blocker.proceed}
        onCancel={() => blocker.reset}
        onSave={() => {
          if (blocker.reset) {
            blocker.reset();
          }
          scheduleSave();
        }}
        title={'Daten der Anamnese behalten?'}
      />
    </>
  );
}

interface FormValues {
  doctorAddress: AddressDto | null;
  doctorContact: AddressContactDto | null;
}

function getDefaultValues(socialAnamnesis: SocialAnamnesisDto) {
  return {
    doctorAddress: socialAnamnesis.doctor?.address ?? null,
    doctorContact: socialAnamnesis.doctor?.contact ?? null,
  };
}

const fieldMetaInfos: FieldMetaInfos<FormValues> = {
  doctorAddress: { debounce: true, ignorePropertiesForComparison: ['id'] },
  doctorContact: { debounce: true, ignorePropertiesForComparison: ['id', 'addressId'] },
};
